/** Duration of animated anchor scrolls (tab clicks, hashchange). Lower = snappier. */
export const SMOOTH_SCROLL_MS = 350;

/** Context Navigation Tabs: these anchor ids scroll to the top of the page. */
export const HOME_ANCHOR_ID = 'home';

const HOME_ANCHOR_IDS = new Set([HOME_ANCHOR_ID, 'strona-glowna']);

export function isHomeAnchor(id) {
  return HOME_ANCHOR_IDS.has(id);
}

function animateScrollToY(targetY, { duration = SMOOTH_SCROLL_MS } = {}) {
  if (typeof window === 'undefined') return;

  const startY = window.scrollY;
  const distance = targetY - startY;

  if (Math.abs(distance) < 1) return;

  const startTime = performance.now();
  const easeOutCubic = (t) => 1 - (1 - t) ** 3;

  const step = (now) => {
    const progress = Math.min((now - startTime) / duration, 1);
    window.scrollTo(0, startY + distance * easeOutCubic(progress));
    if (progress < 1) requestAnimationFrame(step);
  };

  requestAnimationFrame(step);
}

export function smoothScrollToTop({ duration = SMOOTH_SCROLL_MS } = {}) {
  animateScrollToY(0, { duration });
}

export function scrollToTop({ smooth = false, duration = SMOOTH_SCROLL_MS } = {}) {
  if (typeof window === 'undefined') return;

  if (smooth) {
    smoothScrollToTop({ duration });
  } else {
    window.scrollTo(0, 0);
  }
}

/**
 * Scroll so `element` sits at the top of the viewport, honouring its
 * `scroll-margin-top` (e.g. DynamicBlock's `scroll-mt-[64px]`).
 *
 * `extraOffset` reserves additional sticky chrome above the section (e.g. the
 * reveal-on-scroll-up main header sitting above Context Navigation Tabs).
 */
export function getElementScrollTop(element, { extraOffset = 0 } = {}) {
  if (!element || typeof window === 'undefined') return 0;

  const scrollMarginTop = parseFloat(getComputedStyle(element).scrollMarginTop) || 0;
  return (
    element.getBoundingClientRect().top + window.scrollY - scrollMarginTop - extraOffset
  );
}

/** Wait for DOM/layout updates (e.g. collapsing mobile nav) before measuring scroll position. */
export function afterLayoutSettle(callback) {
  if (typeof window === 'undefined') return;

  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
}

/**
 * Custom eased scroll — faster than the browser's native `behavior: 'smooth'`
 * (which is ~500–1000 ms and not configurable).
 */
export function smoothScrollToElement(
  element,
  { duration = SMOOTH_SCROLL_MS, extraOffset = 0 } = {},
) {
  if (!element || typeof window === 'undefined') return;

  animateScrollToY(getElementScrollTop(element, { extraOffset }), { duration });
}

/**
 * Scroll to the element matching `window.location.hash`.
 *
 * Block sections (`<section :id>` from DynamicBlock) mount after the layout,
 * so a single nextTick is not enough on first paint or Inertia navigations.
 * Retries until the target exists or `maxAttempts` is reached.
 *
 * @returns {() => void} cancel function
 */
export function scrollToAnchorFromHash({
  smooth = false,
  duration = SMOOTH_SCROLL_MS,
  maxAttempts = 50,
  intervalMs = 50,
} = {}) {
  if (typeof window === 'undefined') return () => {};

  const { hash } = window.location;
  if (!hash || hash.length < 2) return () => {};

  let id;
  try {
    id = decodeURIComponent(hash.slice(1));
  } catch {
    id = hash.slice(1);
  }

  if (!id) return () => {};

  if (isHomeAnchor(id)) {
    scrollToTop({ smooth, duration });
    return () => {};
  }

  let attempts = 0;
  let timer = null;
  let cancelled = false;

  const tryScroll = () => {
    if (cancelled) return true;

    const el = document.getElementById(id);
    if (!el) return false;

    if (smooth) {
      smoothScrollToElement(el, { duration });
    } else {
      window.scrollTo(0, getElementScrollTop(el));
    }
    return true;
  };

  const schedule = () => {
    if (tryScroll()) return;

    attempts += 1;
    if (attempts >= maxAttempts || cancelled) return;

    timer = window.setTimeout(schedule, intervalMs);
  };

  schedule();

  return () => {
    cancelled = true;
    if (timer) window.clearTimeout(timer);
  };
}

export function parseHashId(hash = window.location.hash) {
  if (!hash || hash.length < 2) return null;

  try {
    return decodeURIComponent(hash.slice(1));
  } catch {
    return hash.slice(1);
  }
}
